import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspaceState } from '@/hooks/useWorkspaceState';
import { ResolutionWarningModal } from '@/components/ResolutionWarningModal';
import { Header } from '@components/Header';
import { Sidebar } from '@components/Sidebar';
import { ChatArea } from '@components/ChatArea';
import { DocumentViewer } from '@components/DocumentViewer';
import { useCompactMode } from '@/hooks/useCompactMode';
import { MpepViewer } from '@components/MpepViewer';
import { CfrViewer } from '@components/CfrViewer';
import { UscViewer } from '@components/UscViewer';
import { DocumentsPage } from '@components/DocumentsPage';
import { SessionsPage } from '@components/SessionsPage';
import { ExaminationPage } from '@components/ExaminationPage';
import { LineNumberedOAViewer } from '@components/LineNumberedOAViewer';
import { ResourcesPage } from '@components/ResourcesPage';
import { TasksPage } from '@components/TasksPage';
import { USPTODataPage } from '@components/USPTODataPage';
import { DraftsPage } from '@components/DraftsPage';
import { IDSPage } from '@components/IDSPage';
import { FormsPage } from '@components/FormsPage';
import { TemplatesPage } from '@components/TemplatesPage';
import { USApplicationDisplay } from '@components/uspto/USApplicationDisplay';
import { Notifications } from '@components/Notifications';
import { DocumentOnboarding } from '@components/DocumentOnboarding';
import { BatchDocumentOnboarding } from '@components/BatchDocumentOnboarding';
import { FileUploadModal } from '@components/FileUploadModal';
import { SaveSessionModal } from '@components/SaveSessionModal';
import { NewSessionWarningModal } from '@components/NewSessionWarningModal';
import { OnboardingWizard } from '@components/OnboardingWizard';
import { QuickStartScreen } from '@components/QuickStartScreen';
import { useUIStore, useDocumentStore, useSessionStore, useNotificationStore, useUserStore } from '@/stores';
import { forgetPendingMessage } from '@/utils/pendingMessage';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { useAuth } from '@context/AuthContext';
import { patentApi, resourceApi, sessionApi, documentApi, taskApi } from '@/services/api';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Button } from '@components/ui/button';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, BookOpen, X, ChevronLeft, ChevronRight, Plus, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PageType = 'chat' | 'documents' | 'uspto_data' | 'examination' | 'resources' | 'tasks' | 'sessions' | 'drafts' | 'ids' | 'forms' | 'templates';

const MAIN_PAGES: PageType[] = ['chat', 'documents', 'uspto_data', 'examination', 'resources', 'tasks', 'sessions', 'drafts', 'ids', 'forms', 'templates'];

interface CfrSection {
  number: string;
  title?: string;
}

interface CfrSubpart {
  id: string;
  title: string;
  sections: CfrSection[];
}

interface CfrPart {
  id: string;
  number: string;
  title: string;
  sections: CfrSection[];
  subparts: CfrSubpart[];
}

const normalizeCfrStructure = (rawStructure: any): CfrPart[] => {
  if (!Array.isArray(rawStructure)) return [];
  const usptoChapter = rawStructure.find((chapter: any) => chapter.number === 'I');
  if (!usptoChapter) return [];

  const parts: CfrPart[] = [];

  usptoChapter.children?.forEach((subchapter: any) => {
    subchapter.children?.forEach((part: any) => {
      if (part.type !== 'part') return;
      const partNumber = part.number || part.name || '';
      const cleanedTitle =
        part.name?.replace(/^PART\s+\d+—?\s*/i, '').trim() ||
        part.name ||
        `Part ${partNumber}`;

      const directSections: CfrSection[] = [];
      const subparts: CfrSubpart[] = [];

      part.children?.forEach((child: any) => {
        if (child.type === 'section') {
          const sectionNumber = (child.number || '').replace(/§\s*/, '').trim();
          if (sectionNumber) {
            directSections.push({
              number: sectionNumber,
              title: child.name,
            });
          }
        } else if (child.type === 'subpart') {
          const subpartSections: CfrSection[] =
            child.children
              ?.filter((subChild: any) => subChild.type === 'section')
              .map((sectionNode: any) => ({
                number: (sectionNode.number || '').replace(/§\s*/, '').trim(),
                title: sectionNode.name,
              }))
              .filter((sec: CfrSection) => !!sec.number) || [];

          if (subpartSections.length > 0) {
            subparts.push({
              id: child.number || child.name,
              title:
                child.name?.replace(/^Subpart\s+[A-Z]+\s*—?\s*/i, '').trim() ||
                child.name ||
                (child.number ? `Subpart ${child.number}` : 'Subpart'),
              sections: subpartSections,
            });
          }
        }
      });

      parts.push({
        id: partNumber,
        number: partNumber,
        title: cleanedTitle,
        sections: directSections,
        subparts,
      });
    });
  });

  return parts.sort((a, b) => parseFloat(a.number) - parseFloat(b.number));
};

export default function Main() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = searchParams.get('view') as PageType;
  const activePage: PageType = MAIN_PAGES.includes(requestedPage) ? requestedPage : 'chat';
  const [tasksVisited, setTasksVisited] = useState(activePage === 'tasks');
  useEffect(() => { if (activePage === 'tasks') setTasksVisited(true); }, [activePage]);
  const setActivePage = useCallback((page: PageType) => {
    if (!MAIN_PAGES.includes(page)) return;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (page === 'chat') next.delete('view');
      else next.set('view', page);
      return next;
    });
  }, [setSearchParams]);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [taskSourceDocument, setTaskSourceDocument] = useState<any>(null);

  // Multi-document support for chat page
  const [selectedChatDocuments, setSelectedChatDocuments] = useState<Array<{
    id: string;
    doc: any;
    type: string;
  }>>([]);
  const [chatDocumentArrangement, setChatDocumentArrangement] = useWorkspaceState<'horizontal' | 'vertical' | 'grid'>('chat-document-arrangement', 'horizontal', value => ['horizontal', 'vertical', 'grid'].includes(String(value)));

  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');
  const [showDocumentOnboarding, setShowDocumentOnboarding] = useState(false);
  const [showBatchDocumentOnboarding, setShowBatchDocumentOnboarding] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveAsMode, setSaveAsMode] = useState(false);
  const [showNewSessionWarning, setShowNewSessionWarning] = useState(false);
  const [sessionContext, setSessionContext] = useState<any>(null);
  const [pendingTaskResult, setPendingTaskResult] = useState<any>(null); // Task result to show in DocumentViewer
  const [documentViewerWidth, setDocumentViewerWidth] = useWorkspaceState('document-viewer-width', 600, value => typeof value === 'number' && Number.isFinite(value) && value >= 280);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useWorkspaceState('sidebar-collapsed', false);
  const quickStartOpenedRef = useRef(false);
  const [mpepViewerState, setMpepViewerState] = useState<{
    section: string;
    title?: string;
    loading: boolean;
    content: any;
    error?: string;
  } | null>(null);
  const [mpepStructure, setMpepStructure] = useState<any>(null);
  const [mpepStructureLoading, setMpepStructureLoading] = useState(false);
  const [cfrViewerState, setCfrViewerState] = useState<{
    section: string;
    title?: string;
    loading: boolean;
    content: any;
    error?: string;
  } | null>(null);
  const [cfrStructure, setCfrStructure] = useState<CfrPart[] | null>(null);
  const [cfrStructureLoading, setCfrStructureLoading] = useState(false);
  const [uscViewerState, setUscViewerState] = useState<{
    section: string;
    title?: string;
    loading: boolean;
    content: any;
    error?: string;
  } | null>(null);
  const [uscStructure, setUscStructure] = useState<any>(null);
  const [uscStructureLoading, setUscStructureLoading] = useState(false);
  const [uscFullInfo, setUscFullInfo] = useState<any>(null); // Store full USC data including content
  const [applicationViewerState, setApplicationViewerState] = useState<{
    applicationData: any;
    pdfUrl?: string | null;
    pdfLoading?: boolean;
    ocrText?: string | null;
    ocrLines?: Array<{line_number: number; page: number; text: string; is_header: boolean}> | null;
    ocrMetadata?: any | null;
    hasLineNumbers?: boolean;
    textLoading?: boolean;
    prosDocInfo?: { appNumber: string; docId: string; rawDoc: any; docName?: string; docDate?: string };
    hideApplicationHeader?: boolean;
  } | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState<number>(0);
  const [prosDocViewMode, setProsDocViewMode] = useState<'pdf' | 'text' | 'both'>('pdf');
  const [pdfScale, setPdfScale] = useState<number>(1.0);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [examinationApplicationNumber, setExaminationApplicationNumber] = useState<string | undefined>(undefined);
  const { user, loading: authLoading, logout } = useAuth();
  const { compactMode } = useCompactMode();

  // Helper functions for managing chat documents
  const addChatDocument = (doc: any, type: string) => {
    const id = `${type}-${doc.id || doc.file_id || doc.docId || doc.resource_id || doc.publication_number || doc.patent_number || Math.random()}`;

    // Check if this document is already selected
    const existingIndex = selectedChatDocuments.findIndex(chatDoc => chatDoc.id === id);
    if (existingIndex !== -1) {
      // Document already open - update it with new scroll info if provided
      if (doc.initialTab || doc.scrollToId) {
        setSelectedChatDocuments(prev => {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            doc: {
              ...updated[existingIndex].doc,
              initialTab: doc.initialTab,
              scrollToId: doc.scrollToId,
              _scrollTimestamp: Date.now() // Force prop change
            }
          };
          return updated;
        });
      }
      return; // Don't add duplicates
    }

    setSelectedChatDocuments(prev => [...prev, { id, doc, type }]);
    setShowDocWindow(true);
  };

  const removeChatDocument = (id: string) => {
    setSelectedChatDocuments(prev => {
      const newDocs = prev.filter(chatDoc => chatDoc.id !== id);
      // If no documents left, close the document window
      if (newDocs.length === 0) {
        setShowDocWindow(false);
      }
      return newDocs;
    });
  };

  const removeAllChatDocuments = () => {
    setSelectedChatDocuments([]);
    setShowDocWindow(false);
  };

  const hasCompletedOnboarding = useUserStore(state =>
    state.userSettings.hasCompletedOnboarding as boolean | undefined
  );
  const showWizardOnStartup = useUserStore(state =>
    state.userSettings.showWizardOnStartup !== false // Default to true if not set
  );
  const updateUserSettings = useUserStore(state => state.updateUserSettings);

  const showDocWindow = useDocumentStore(state => state.showDocWindow);
  const setShowDocWindow = useDocumentStore(state => state.setShowDocWindow);

  const chatMessages = useSessionStore(state => state.chatMessages);
  const sessionId = useSessionStore(state => state.sessionId);
  useEffect(() => { setTaskSourceDocument(null); }, [sessionId]);
  const sessionName = useSessionStore(state => state.sessionName);
  const sessionHasBeenSaved = useSessionStore(state => state.sessionHasBeenSaved);
  const sessionIsDirty = useSessionStore(state => state.hasUnsavedChanges);
  const savedFolderForSession = useSessionStore(state => state.savedFolderForSession);
  const isUserSaved = useSessionStore(state => state.isUserSaved);
  const sessionDocuments = useSessionStore(state => state.sessionDocuments);
  const sessionResources = useSessionStore(state => state.sessionResources);
  const setSessionApplications = useSessionStore(state => state.setSessionApplications);
  const sessionApplications = useSessionStore(state => state.sessionApplications);
  const resetSession = useSessionStore(state => state.resetSession);
  const setSessionDocuments = useSessionStore(state => state.setSessionDocuments);
  const setSessionResources = useSessionStore(state => state.setSessionResources);
  const setSessionHasBeenSaved = useSessionStore(state => state.setSessionHasBeenSaved);
  const autoSaveSession = useSessionStore(state => state.autoSaveSession);
  const autoSaveRegularSession = useSessionStore(state => state.autoSaveRegularSession);
  const userSettings = useUserStore(state => state.userSettings);
  const addNotification = useNotificationStore(state => state.addNotification);
  const startPolling = useJobPollingStore(state => state.startPolling);

  // Fetch session applications on mount
  useEffect(() => {
    const loadSessionApplications = async () => {
      try {
        // Override cache on page load to get fresh data
        const applications = await patentApi.getSessionApplications(true);
        setSessionApplications(applications);
      } catch (error) {
        console.error('Failed to load session applications:', error);
      }
    };

    loadSessionApplications();
  }, [setSessionApplications]);

  // Load user settings from backend on mount
  useEffect(() => {
    const { loadUserSettingsFromBackend } = useUserStore.getState();
    console.log('[Main] Loading user settings from backend on mount');
    loadUserSettingsFromBackend();
  }, []);

  // Extract prosecution documents from session documents and store for chat
  useEffect(() => {
    try {
      const prosDocuments = (sessionDocuments as any[])?.filter(
        (doc: any) => doc.resource_type === 'pros_doc' || doc.resource_type === 'prosecution_document'
      ) || [];

      const prosDocIds = prosDocuments.map((doc: any) => doc.resource_id);
      const prosDocNames = prosDocuments.map((doc: any) => doc.resource_name || doc.resource_id);

      // Store in localStorage for chat API to use
      if (prosDocIds.length > 0) {
        localStorage.setItem('new_pros_docs', JSON.stringify(prosDocIds));
        localStorage.setItem('new_pros_doc_names', JSON.stringify(prosDocNames));
      } else {
        // Clear if no prosecution docs
        localStorage.removeItem('new_pros_docs');
        localStorage.removeItem('new_pros_doc_names');
      }
    } catch (error) {
      console.error('Failed to extract prosecution documents:', error);
    }
  }, [sessionDocuments]);

  // Auto-save when documents/applications change
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      // Don't auto-save on initial mount (when all are empty arrays)
      if (sessionDocuments.length === 0 && sessionResources.length === 0 && sessionApplications.length === 0) {
        return;
      }

      console.log('[Main] Documents changed, triggering auto-save');

      // Auto-save unsaved sessions (creates "unsaved-" session)
      autoSaveSession();

      // Auto-save regular sessions if setting is enabled
      if (userSettings.autoSaveEnabled === true) {
        console.log('[Main] Auto-save enabled, saving regular session');
        autoSaveRegularSession();
      }
    }, 2000); // Debounce 2 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [sessionDocuments, sessionResources, sessionApplications, autoSaveSession, autoSaveRegularSession, userSettings]);

  useEffect(() => {
    const handleSessionExpired = (event: any) => {
      const message = event.detail?.message || 'Your session has expired. Please login again.';
      setSessionExpiredMessage(message);
      setShowSessionExpiredModal(true);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);
    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    let previousContext = localStorage.getItem('session_context');
    let previousSessionId = useSessionStore.getState().sessionId;
    const loadSessionContext = () => {
      const storedData = localStorage.getItem('session_context');
      const state = useSessionStore.getState();
      if (storedData !== previousContext && state.sessionId === previousSessionId) state.markSessionDirty();
      previousContext = storedData;
      previousSessionId = state.sessionId;
      if (storedData && storedData !== "undefined") {
        try {
          const parsedData = JSON.parse(storedData);
          setSessionContext(parsedData);
        } catch (error) {
          console.error('Failed to parse session context:', error);
          setSessionContext(null);
        }
      } else {
        setSessionContext(null);
      }
    };

    loadSessionContext();

    // Listen for context updates
    const handleStorageUpdate = () => loadSessionContext();
    window.addEventListener('storage-update', handleStorageUpdate);
    window.addEventListener('storage', loadSessionContext);

    return () => {
      window.removeEventListener('storage-update', handleStorageUpdate);
      window.removeEventListener('storage', loadSessionContext);
    };
  }, []);

  // Listen for view-document-with-task events from task result panels
  useEffect(() => {
    const handleViewDocumentWithTask = (e: Event) => {
      const { document: doc, taskResult } = (e as CustomEvent).detail;
      setPendingTaskResult(taskResult);
      setActivePage('documents');
      // Open the document in the viewer
      setMpepViewerState(null);
      setCfrViewerState(null);
      setUscViewerState(null);
      setApplicationViewerState(null);
      setSelectedDocument(doc);
      setShowDocWindow(true);
    };
    window.addEventListener('view-document-with-task', handleViewDocumentWithTask);
    return () => window.removeEventListener('view-document-with-task', handleViewDocumentWithTask);
  }, []);

  // Clear pendingTaskResult after it's been consumed by DocumentViewer
  useEffect(() => {
    if (pendingTaskResult && activePage !== 'documents') {
      setPendingTaskResult(null);
    }
  }, [activePage, pendingTaskResult]);

  // Check if user needs Quick Start on first login
  useEffect(() => {
    if (user && !authLoading) {
      // Check if we should show quick start on startup
      const shouldShowOnStartup = showWizardOnStartup && chatMessages.length <= 1 && !sessionId;

      if (shouldShowOnStartup) {
        // Small delay to let the UI settle before showing Quick Start
        const timer = setTimeout(() => {
          setShowQuickStart(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
    // Note: showWizardOnStartup is intentionally NOT in the dependency array
    // We only want to check it on initial login, not when the setting is toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, hasCompletedOnboarding]);

  // Listen for manual Quick Start reopen from menu
  useEffect(() => {
    const handleReopenQuickStart = () => setShowQuickStart(true);
    window.addEventListener('open-onboarding-wizard', handleReopenQuickStart);
    return () => window.removeEventListener('open-onboarding-wizard', handleReopenQuickStart);
  }, []);

  // Mark onboarding as complete when Quick Start closes
  useEffect(() => {
    if (showQuickStart) quickStartOpenedRef.current = true;
    if (!showQuickStart && quickStartOpenedRef.current && user && hasCompletedOnboarding === false) {
      updateUserSettings('hasCompletedOnboarding', true);
    }
  }, [showQuickStart, user, hasCompletedOnboarding, updateUserSettings]);

  // Clear examination application number when navigating away from examination page
  useEffect(() => {
    if (activePage !== 'examination') {
      setExaminationApplicationNumber(undefined);
    }
  }, [activePage]);

  // Clear selected document when navigating to documents page so no blank viewer appears
  useEffect(() => {
    if (activePage === 'documents') {
      setSelectedDocument(null);
    }
  }, [activePage]);

  useEffect(() => {
    const handleViewportResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, []);

  const [tasksOpenJobId, setTasksOpenJobId] = useState<string | null>(null);
  const [tasksOpenTaskId, setTasksOpenTaskId] = useState<string | null>(null);

  // Navigate-page event listener
  useEffect(() => {
    const handleNavigatePage = (e: CustomEvent) => {
      const { page, jobId, taskId } = e.detail;
      setActivePage(page as PageType);
      if (page === 'tasks' && jobId) {
        setTasksOpenJobId(jobId);
      }
      if (page === 'tasks' && taskId) {
        setTasksOpenTaskId(taskId);
      }
    };
    window.addEventListener('navigate-page', handleNavigatePage as EventListener);
    return () => {
      window.removeEventListener('navigate-page', handleNavigatePage as EventListener);
    };
  }, [setActivePage]);

  // Global job completion listener - persists across page navigation
  useEffect(() => {
    const handleJobComplete = async (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      console.log('[Main] Job completion event received:', event.detail);

      // Skip notifications for jobs without a docType (like prosecution documents)
      // Those are handled by their own specific completion handlers
      if (!docType || docType === 'undefined') {
        console.log('[Main] Skipping notification for job without docType:', jobId);
        return;
      }

      // Handle multi-doc analysis job completion
      if (docType === 'multi_doc_analysis') {
        if (success) {
          addNotification({
            type: 'success',
            title: 'Multi-Doc Q&A Complete',
            message: `Results ready for: ${docId}`,
            duration: 0,
            action: { label: 'View in Tasks', navigateTo: 'tasks', jobId },
          });
        } else {
          addNotification({
            type: 'error',
            title: 'Multi-Doc Q&A Failed',
            message: `Analysis failed for: ${docId}`,
            duration: 8000,
          });
        }
        return;
      }

      // Format resource type name: convert underscores to spaces and capitalize each word
      const resourceTypeName = docType
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (success) {
        // Remove from loading documents tracking
        removeLoadingDocument(docId, resourceTypeName);

        addNotification({
          type: 'success',
          title: `${resourceTypeName} Ready`,
          message: `${docId} has been loaded and is ready to use.`,
          duration: 5000,
        });

        // If an application was loaded, refresh session applications
        if (docType === 'application') {
          try {
            const applications = await patentApi.getSessionApplications(true);
            setSessionApplications(applications);
            console.log('[Main] Session applications refreshed after application load');
          } catch (err) {
            console.error('Failed to refresh session applications:', err);
          }
        }

        // Trigger session context reload to update UI
        window.dispatchEvent(new Event('storage-update'));
      } else {
        let errorMessage = `Failed to load ${docId}. Please try again.`;

        if (error === '404') {
          errorMessage = `${docId} not found. This may be an older document. Please verify the number is correct.`;
        } else if (error === 'timeout') {
          addNotification({
            type: 'warning',
            title: 'Loading Timeout',
            message: `${docId} loading timed out. It may still complete in the background.`,
            duration: 5000,
          });
          // Don't remove from loading - it may still complete
          return;
        } else if (error === 'polling-error') {
          errorMessage = `Error checking status for ${docId}.`;
        }

        // Remove from loading documents tracking on failure
        removeLoadingDocument(docId, resourceTypeName);

        addNotification({
          type: 'error',
          title: `${resourceTypeName} Load Failed`,
          message: errorMessage,
          duration: 8000,
        });
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [addNotification, setSessionApplications]);

  const handleSessionExpiredConfirm = () => {
    setShowSessionExpiredModal(false);
    logout();
  };

  // Session control handlers
  const handleNewSession = () => {
    // Always show modal to let user choose document handling
    setShowNewSessionWarning(true);
  };

  const handleSave = async () => {
    const savingState = useSessionStore.getState();
    if (savingState.isAutoSaving) return;
    // For unsaved sessions (identified by "unsaved-" prefix), Save button opens Save As modal
    if (sessionId?.startsWith('unsaved-')) {
      handleSaveAs();
      return;
    }

    if (sessionHasBeenSaved && sessionId && sessionName) {
      let revision = savingState.beginSessionSave();
      // Session has been saved before - save directly without modal
      try {
        // Update session context to include current active drafts
        const sessionContextStr = localStorage.getItem('session_context');
        const sessionContext = sessionContextStr ? JSON.parse(sessionContextStr) : {};
        const activeDraftsStr = localStorage.getItem('active_drafts');
        const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];
        sessionContext.current_drafts = activeDrafts;
        localStorage.setItem('session_context', JSON.stringify(sessionContext));
        window.dispatchEvent(new Event('storage-update'));
        revision = useSessionStore.getState().sessionRevision;
        console.log('[Main.handleSave] Saving session with drafts:', activeDrafts);

        await sessionApi.saveSession(
          sessionId,
          sessionName,
          chatMessages,
          savedFolderForSession
        );
        if (useSessionStore.getState().sessionId !== sessionId) return;
        setSessionHasBeenSaved(true);
        savingState.finishSessionSave(revision);

        // Dispatch event to update session list
        window.dispatchEvent(new Event('sessions-updated'));
      } catch (error) {
        console.error('Failed to save session:', error);
        if (useSessionStore.getState().sessionId === sessionId) savingState.failSessionSave('Could not save your changes. Please retry.');
      }
    } else {
      // First time save - show modal for name input
      setSaveAsMode(false);
      setShowSaveModal(true);
    }
  };

  const handleSaveAs = () => {
    setSaveAsMode(true);
    setShowSaveModal(true);
  };

  // Keyboard shortcut for Ctrl+S to save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+S (or Cmd+S on Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's default save behavior

        // If session ID starts with "unsaved-", open Save As modal
        // Otherwise, use regular save (which will also handle the logic internally)
        if (sessionId?.startsWith('unsaved-')) {
          handleSaveAs();
        } else {
          handleSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sessionHasBeenSaved, sessionId, sessionName, chatMessages, savedFolderForSession]);

  const handleNewSessionConfirm = (clearDocuments: boolean) => {
    setTaskSourceDocument(null);
    // User confirmed to start new session

    if (clearDocuments) {
      // Clear all documents AND drafts
      setSessionDocuments([]);
      setSessionResources([]);
      setSessionApplications([]);

      // Dispatch event to clear active drafts state in ChatArea
      window.dispatchEvent(new CustomEvent('clear-active-drafts'));

      // Also clear session_context from localStorage
      const context = {
        current_patents: [],
        current_applications: [],
        current_publications: [],
        current_foreign_patents: [],
        current_drafts: [],
        resource_ids: [],
        resource_names: [],
        new_resource_ids: [],
        new_resource_names: [],
        mpep_sections: [],
        cfr_sections: [],
        usc_sections: [],
        fed_reg_sections: [],
        pros_docs: [],
        patent_numbers_dict_arr: [],
        pros_doc_names: [],
        extracted_resources: [],
        current_patent: null,
        current_publication: null,
        current_application: null,
        last_context_type: null,
        last_context_number: null,
      };

      localStorage.setItem('session_context', JSON.stringify(context));

      // Also clear separate localStorage keys
      localStorage.removeItem('new_resource_ids');
      localStorage.removeItem('new_resource_names');
      localStorage.removeItem('patents');
    }
    // else: Keep documents AND drafts - no changes needed

    // Reset session
    resetSession();

    // And stop waiting for the answer the old session was waiting for. Without this the
    // reload below picks the poll back up, and the new session opens with a loading
    // indicator for a message that is not in it.
    forgetPendingMessage();

    // Clear active drafts from localStorage only if clearing documents
    // Use setTimeout to ensure all React effects have completed
    setTimeout(() => {
      if (clearDocuments) {
        localStorage.setItem('active_drafts', JSON.stringify([]));
      }
      window.location.reload();
    }, 100);
  };

  const handleNewSessionSaveFirst = () => {
    setSaveAsMode(false);
    setShowSaveModal(true);
  };

  const handlePasteQuestionFromWizard = (question: string) => {
    // Paste the question into chat input by dispatching a custom event
    // The ChatArea component will listen for this event
    window.dispatchEvent(new CustomEvent('paste-question', { detail: { question } }));

    // Switch to chat view so user can see the input
    setActivePage('chat');
  };

  const handleOpenDocumentModalFromWizard = () => {
    // Open the document onboarding modal
    setShowDocumentOnboarding(true);
  };

  const handleOpenUploadModalFromWizard = () => {
    // Open the file upload modal
    setShowFileUploadModal(true);
  };

  const handleNavigateToPageFromWizard = (page: 'uspto_data' | 'resources' | 'examination' | 'tasks' | 'ids' | 'forms', applicationNumber?: string, taskId?: string) => {
    console.log('[Main] Navigate to page:', page, 'with application:', applicationNumber, 'task:', taskId);

    // Navigate to the specified page first
    setActivePage(page);

    // If navigating to examination with an application number, dispatch event after navigation
    if (page === 'examination' && applicationNumber) {
      console.log('[Main] Dispatching select-examination-application event for:', applicationNumber);
      // Use setTimeout to ensure the page has rendered before dispatching the event
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('select-examination-application', {
          detail: { applicationNumber }
        }));
      }, 100);
    }

    // If navigating to tasks with a taskId, dispatch event to select that task
    if (page === 'tasks' && taskId) {
      console.log('[Main] Dispatching select-task event for:', taskId);
      // Use setTimeout to ensure the page has rendered before dispatching the event
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('select-task', {
          detail: { taskId }
        }));
      }, 100);
    }
  };

  const handleAddApplicationFromWizard = async (appNumber: string): Promise<void> => {
    // Clean the application number
    let cleanedId = appNumber
      .replace(/,/g, '')
      .replace(/\//g, '')
      .replace(/^US/i, '')
      .replace(/[A-Z]\d*$/i, '');

    // Validate format (8 digits)
    const pattern = /^\d{8}$/;
    if (!pattern.test(cleanedId)) {
      throw new Error('Application number must be 8 digits (e.g., 16123456 or 16/123,456)');
    }

    try {
      // Call API to fetch and load the application
      const response = await documentApi.fetchAndLoadDocument('application', cleanedId);

      if (response?.job_id) {
        // Add to session context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
        context.current_applications = [...(context.current_applications || []), cleanedId];

        // Update context immediately so it shows in UI
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        // Show processing notification
        addNotification({
          type: 'info',
          title: 'Loading Application',
          message: `Processing ${cleanedId}...`,
          duration: 60000,
        });

        // Add to loading documents tracking
        addLoadingDocument(cleanedId, 'Application', cleanedId);

        // Start polling for job status
        startPolling(response.job_id, 'application', cleanedId);
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (error) {
      console.error(`Failed to load application ${cleanedId}:`, error);
      throw error;
    }
  };

  const handlePageChange = (page: PageType) => {
    setActivePage(page);
  };

  const extractMpepSection = (doc: any): { section: string | null; title?: string } => {
    if (!doc) return { section: null };
    if (doc.section) return { section: String(doc.section), title: doc.title };

    const possibleSources = [
      doc.title,
      doc.file_name,
      doc.resource_id,
      doc.doc_id,
      doc.id,
    ];

    for (const source of possibleSources) {
      if (typeof source !== 'string') continue;
      const match = source.match(/MPEP\s*§?\s*([0-9.]+)/i) || source.match(/([0-9.]+)$/);
      if (match) {
        return { section: match[1], title: doc.title || `MPEP §${match[1]}` };
      }
    }
    return { section: null, title: doc.title };
  };

  const extractCfrSection = (doc: any): { section: string | null; title?: string } => {
    if (!doc) return { section: null };
    const candidates = [
      doc.section,
      doc.doc_id,
      doc.resource_id,
      typeof doc.title === 'string' ? doc.title : null,
    ];

    for (const candidate of candidates) {
      if (typeof candidate !== 'string') continue;
      const match = candidate.match(/(\d+\.\d+[a-z0-9()\-.]*)/i);
      if (match) {
        return { section: match[1], title: doc.title };
      }
    }

    return { section: null, title: doc.title };
  };

  const ensureMpepStructure = async () => {
    if (mpepStructure || mpepStructureLoading) return;
    try {
      setMpepStructureLoading(true);
      const structure = await resourceApi.fetchMpepInfo();
      setMpepStructure(structure);
    } catch (error) {
      console.error('Failed to load MPEP structure:', error);
    } finally {
      setMpepStructureLoading(false);
    }
  };

  const ensureCfrStructure = async () => {
    if (cfrStructure || cfrStructureLoading) return;
    setCfrStructureLoading(true);
    try {
      const response = await fetch('/cfr_info.json');
      if (!response.ok) {
        throw new Error('Failed to load CFR structure');
      }
      const data = await response.json();
      setCfrStructure(normalizeCfrStructure(data));
    } catch (error) {
      console.error('Failed to load CFR structure:', error);
    } finally {
      setCfrStructureLoading(false);
    }
  };

  const loadMpepSection = async (section: string, title?: string) => {
    // Extract only the integer part before the first decimal point
    const normalizedSection = section.replace(/[^0-9.]/g, '').split('.')[0];
    if (!normalizedSection) {
      setMpepViewerState({
        section,
        title,
        loading: false,
        content: null,
        error: 'Invalid section number.',
      });
      return;
    }

    // Update title to reflect the actual section being loaded
    const normalizedTitle = `MPEP ${normalizedSection}`;

    setMpepViewerState({
      section: normalizedSection,
      title: normalizedTitle,
      loading: true,
      content: null,
      error: undefined,
    });

    try {
      const content = await resourceApi.fetchMpepContent(normalizedSection);
      if (!content) {
        setMpepViewerState(prev => prev ? { ...prev, loading: false, error: 'No content returned for this section.' } : prev);
        return;
      }
      setMpepViewerState(prev => prev ? { ...prev, loading: false, content, error: undefined } : prev);
    } catch (error) {
      console.error('Failed to load MPEP section:', error);
      setMpepViewerState(prev => prev ? { ...prev, loading: false, error: 'Failed to load section. Please try again.' } : prev);
    }
  };

  const loadCfrSection = async (section: string, title?: string) => {
    const normalizedSection = section.replace(/[^0-9.\w()-]/g, '');
    if (!normalizedSection) {
      setCfrViewerState({
        section,
        title,
        loading: false,
        content: null,
        error: 'Invalid section number.',
      });
      return;
    }

    setCfrViewerState({
      section: normalizedSection,
      title,
      loading: true,
      content: null,
      error: undefined,
    });

    try {
      const content = await resourceApi.fetchCfrContent(normalizedSection);
      if (!content) {
        setCfrViewerState((prev) =>
          prev ? { ...prev, loading: false, error: 'No content returned for this section.' } : prev
        );
        return;
      }
      setCfrViewerState((prev) =>
        prev ? { ...prev, loading: false, content, error: undefined } : prev
      );
    } catch (error) {
      console.error('Failed to load CFR section:', error);
      setCfrViewerState((prev) =>
        prev ? { ...prev, loading: false, error: 'Failed to load section. Please try again.' } : prev
      );
    }
  };

  const extractUscSection = (doc: any): { section: string | null; title?: string } => {
    if (!doc) return { section: null };

    // First try direct properties that should contain just the section number
    const directCandidates = [doc.section, doc.resource_id];
    for (const candidate of directCandidates) {
      if (typeof candidate === 'string') {
        const match = candidate.match(/(\d+[a-z]?)/i);
        if (match) {
          return { section: match[1], title: doc.title };
        }
      }
    }

    // Then try the title with a more specific pattern (e.g., "35 USC 112")
    if (typeof doc.title === 'string') {
      const uscMatch = doc.title.match(/(?:35\s+)?U\.?S\.?C\.?\s*§?\s*(\d+[a-z]?)/i);
      if (uscMatch) {
        return { section: uscMatch[1], title: doc.title };
      }
    }

    // Finally try other generic fields
    const genericCandidates = [doc.doc_id, doc.id];
    for (const candidate of genericCandidates) {
      if (typeof candidate === 'string') {
        const match = candidate.match(/(\d+[a-z]?)/i);
        if (match) {
          return { section: match[1], title: doc.title };
        }
      }
    }

    return { section: null, title: doc.title };
  };

  const ensureUscStructure = async () => {
    if ((uscStructure && uscFullInfo) || uscStructureLoading) return;
    setUscStructureLoading(true);
    try {
      const data = await resourceApi.fetchUscInfo();

      // Store the full USC info for content lookup
      setUscFullInfo(data);

      // Check if there's a separate structure property for navigation
      if (data && data.structure && Array.isArray(data.structure)) {
        setUscStructure(data.structure);
      } else if (data && data.usc_info) {
        // Build structure from available sections
        // Title 35 USC is organized into 5 parts
        const availableSections = Object.keys(data.usc_info)
          .map(k => parseInt(k, 10))
          .filter(n => !isNaN(n))
          .sort((a, b) => a - b);

        const structure = [
          {
            part_number: 'I',
            part_title: 'United States Patent and Trademark Office',
            sections: availableSections
              .filter(n => n >= 1 && n <= 14)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          },
          {
            part_number: 'II',
            part_title: 'Patentability of Inventions and Grant of Patents',
            sections: availableSections
              .filter(n => n >= 100 && n <= 212)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          },
          {
            part_number: 'III',
            part_title: 'Patents and Protection of Patent Rights',
            sections: availableSections
              .filter(n => n >= 251 && n <= 329)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          },
          {
            part_number: 'IV',
            part_title: 'Patent Cooperation Treaty',
            sections: availableSections
              .filter(n => n >= 351 && n <= 376)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          },
          {
            part_number: 'V',
            part_title: 'Hague Agreement - International Design Registration',
            sections: availableSections
              .filter(n => n >= 381 && n <= 390)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          }
        ].filter(part => part.sections.length > 0); // Only include parts with available sections

        setUscStructure(structure);
      } else {
        console.warn('USC structure not in expected format:', data);
        setUscStructure([]);
      }
    } catch (error) {
      console.error('Failed to load USC structure:', error);
      setUscStructure([]);
      setUscFullInfo(null);
    } finally {
      setUscStructureLoading(false);
    }
  };

  const loadUscSection = async (section: string, title?: string) => {
    const normalizedSection = section.replace(/[^0-9a-z]/gi, '');
    if (!normalizedSection) {
      setUscViewerState({
        section,
        title,
        loading: false,
        content: null,
        error: 'Invalid section number.',
      });
      return;
    }

    setUscViewerState({
      section: normalizedSection,
      title,
      loading: true,
      content: null,
      error: undefined,
    });

    try {
      // USC content is embedded in the full info structure, not fetched separately
      const currentUscInfo = await resourceApi.fetchUscInfo();

      // Store it for future use
      if (!uscFullInfo) {
        setUscFullInfo(currentUscInfo);
      }

      // Extract section content from usc_info object
      // The keys are numeric (1, 2, 3, ..., 112, etc.)
      const numericSection = parseInt(normalizedSection, 10);
      let sectionData = null;

      if (!isNaN(numericSection)) {
        // Try numeric key first
        sectionData = currentUscInfo?.usc_info?.[numericSection];
      }

      // If not found, try string key
      if (!sectionData) {
        sectionData = currentUscInfo?.usc_info?.[normalizedSection];
      }

      if (!sectionData) {
        setUscViewerState({
          section: normalizedSection,
          title: title || `35 U.S.C. § ${normalizedSection}`,
          loading: false,
          content: null,
          error: `Section ${normalizedSection} not found in the database.`,
        });
        return;
      }

      // Format content similar to how ResourcesPage does it
      const content = {
        header: sectionData.header,
        content: sectionData.content,
        raw_html: sectionData.raw_html,
        text: sectionData.content || sectionData.raw_html
      };

      setUscViewerState({
        section: normalizedSection,
        title: title || `35 U.S.C. § ${normalizedSection}`,
        loading: false,
        content,
        error: undefined,
      });
    } catch (error) {
      console.error('Failed to load USC section:', error);
      setUscViewerState({
        section: normalizedSection,
        title: title || `35 U.S.C. § ${normalizedSection}`,
        loading: false,
        content: null,
        error: 'Failed to load section. Please try again.',
      });
    }
  };

  const handleDocumentSelect = (doc: any) => {
    setTaskSourceDocument(doc);
    console.log('Main handleDocumentSelect - received document:', doc);
    const docType = (doc?.type || doc?.file_type || '').toString().toLowerCase();
    if (docType === 'mpep' || (doc?.title && doc.title.toLowerCase().includes('mpep'))) {
      const { section, title } = extractMpepSection(doc);
      if (section) {
        ensureMpepStructure();
        setCfrViewerState(null);
        setUscViewerState(null);
        setApplicationViewerState(null);
        loadMpepSection(section, title);
        setShowDocWindow(false);
      } else {
        addNotification({
          type: 'error',
          title: 'Unable to open MPEP section',
          message: 'This entry does not include a recognizable section number.',
        });
      }
      return;
    }

    if (docType.includes('cfr') || (doc?.title && doc.title.toLowerCase().includes('cfr'))) {
      const { section, title } = extractCfrSection(doc);
      if (section) {
        ensureCfrStructure();
        setMpepViewerState(null);
        setUscViewerState(null);
        setApplicationViewerState(null);
        loadCfrSection(section, title);
        setShowDocWindow(false);
      } else {
        addNotification({
          type: 'error',
          title: 'Unable to open 37 CFR section',
          message: 'This entry does not include a recognizable section number.',
        });
      }
      return;
    }

    if (docType.includes('usc') || (doc?.title && doc.title.toLowerCase().includes('usc'))) {
      const { section, title } = extractUscSection(doc);
      if (section) {
        ensureUscStructure();
        setMpepViewerState(null);
        setCfrViewerState(null);
        setApplicationViewerState(null);
        loadUscSection(section, title);
        setShowDocWindow(false);
      } else {
        addNotification({
          type: 'error',
          title: 'Unable to open 35 USC section',
          message: 'This entry does not include a recognizable section number.',
        });
      }
      return;
    }

    // Handle prosecution documents - open PDF viewer
    if (docType === 'prosecution doc' || docType === 'pros_doc' || doc.file_type === 'pros_doc') {
      setMpepViewerState(null);
      setCfrViewerState(null);
      setUscViewerState(null);
      setShowDocWindow(false);

      // Extract application number and document ID from resource_id
      // Format: {applicationNumber}-{documentId}
      const resourceId = doc.resource_id || '';
      const parts = resourceId.split('-');

      if (parts.length >= 2) {
        const appNumber = parts[0];
        const docId = parts.slice(1).join('-'); // In case docId contains dashes

        // Find the application data from sessionApplications
        const sessionApp = sessionApplications?.find(app =>
          (app.application_id || app.application?.applicationNumberText) === appNumber
        );

        // Set application viewer state first with prosDocInfo
        // Note: When opening from sidebar, we don't have the actual USPTO document object yet
        // so we pass null for rawDoc - the backend will fetch it when needed
        const prosDocInfo = {
          appNumber,
          docId,
          rawDoc: null, // Will be fetched by backend when needed
          docName: doc.description || doc.file_name || doc.title || 'Prosecution Document',
          docDate: doc.date || doc.officialDate || undefined
        };

        // Hide header elements when opening from USPTO Data page
        const hideHeader = activePage === 'uspto_data';

        if (sessionApp && sessionApp.application) {
          setApplicationViewerState({
            applicationData: {
              application_number: appNumber,
              patent_info: {
                applicationMetaData: sessionApp.application.applicationMetaData || {}
              },
              pgpubDocumentMetaData: sessionApp.application.pgpubDocumentMetaData,
              grantDocumentMetaData: sessionApp.application.grantDocumentMetaData,
            },
            pdfLoading: true,
            prosDocInfo,
            hideApplicationHeader: hideHeader,
          });
        } else {
          setApplicationViewerState({
            applicationData: {
              application_number: appNumber,
              patent_info: {
                applicationMetaData: {}
              }
            },
            pdfLoading: true,
            prosDocInfo,
            hideApplicationHeader: hideHeader,
          });
        }

        // Load the prosecution document PDF using job-based polling
        (async () => {
          try {
            // Start the PDF generation job
            const response = await patentApi.getProsecutionDocPdf(appNumber, docId);

            if (response?.job_id) {
              // Start polling for the result
              taskApi.startGraduatedPolling(
                async () => {
                  const result = await patentApi.getPdfJobStatus(response.job_id);

                  // Check if job is complete
                  if (result?.status && result.status.toLowerCase().startsWith('complete')) {
                    return result;
                  }

                  // Return result if there's an error status (stops polling)
                  if (result?.status && result.status.toLowerCase().includes('error')) {
                    return result;
                  }

                  // Still processing, return null to continue polling
                  return null;
                },
                async (result) => {
                  // Handle the result when polling stops
                  if (result?.status && result.status.toLowerCase().startsWith('complete')) {
                    try {
                      // Download the PDF
                      const pdfUrl = await patentApi.downloadProsecutionDocPdf(appNumber, docId);
                      setApplicationViewerState(prev => prev ? { ...prev, pdfUrl, pdfLoading: false } : null);

                      const docName = doc.description || doc.file_name || doc.title || 'Prosecution document';
                      addNotification({
                        type: 'success',
                        title: 'Document Ready',
                        message: `${docName} is ready to view`,
                      });
                    } catch (error) {
                      console.error('Error downloading prosecution PDF:', error);
                      setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
                      addNotification({
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to download prosecution document PDF',
                      });
                    }
                  } else if (result?.status && result.status.toLowerCase().includes('error')) {
                    setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
                    addNotification({
                      type: 'error',
                      title: 'Error',
                      message: `Failed to load document PDF: ${result.status}`,
                    });
                  }
                },
                (error) => {
                  console.error('Polling error:', error);
                  setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
                  addNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to load prosecution document',
                  });
                }
              );
            } else {
              setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
              addNotification({
                type: 'error',
                title: 'Error',
                message: 'Failed to start PDF generation',
              });
            }
          } catch (error) {
            console.error('Failed to load prosecution document:', error);
            setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
            addNotification({
              type: 'error',
              title: 'Error',
              message: 'Failed to load prosecution document',
            });
          }
        })();
      }

      return;
    }

    // Handle applications - show USPTO Data
    if (docType === 'application') {
      setMpepViewerState(null);
      setCfrViewerState(null);
      setUscViewerState(null);
      setShowDocWindow(false);

      // Extract application number from resource_id or title
      let appNumber = doc.resource_id || doc.title || '';
      appNumber = appNumber.replace(/^application-/i, '').trim();

      // Find the application data from sessionApplications
      const sessionApp = sessionApplications?.find(app =>
        (app.application_id || app.application?.applicationNumberText) === appNumber
      );

      if (sessionApp && sessionApp.application) {
        // Map session application structure to USApplicationDisplay expected structure
        setApplicationViewerState({
          applicationData: {
            application_number: appNumber,
            patent_info: {
              applicationMetaData: sessionApp.application.applicationMetaData || {}
            },
            pgpubDocumentMetaData: sessionApp.application.pgpubDocumentMetaData,
            grantDocumentMetaData: sessionApp.application.grantDocumentMetaData,
          }
        });
      } else {
        // If not found in session, fetch from backend
        console.log(`Application ${appNumber} not in session, fetching from backend...`);
        // Don't return - fall through to normal fetch logic below
      }

      // Continue to fetch from backend if not found in session
      if (sessionApp && sessionApp.application) {
        return; // Already loaded from session
      }
    }

    // Handle application type separately - open Application Data view
    if (docType === 'application') {
      const appNumber = doc.id || doc.resource_id || doc.application_number;
      if (appNumber) {
        handleViewApplicationFromDocument(appNumber);
      }
      return;
    }

    setMpepViewerState(null);
    setCfrViewerState(null);
    setUscViewerState(null);
    setApplicationViewerState(null);

    // On chat page, support multiple documents
    if (activePage === 'chat') {
      addChatDocument(doc, docType || 'unknown');
    } else {
      // On other pages, use single document viewer
      setSelectedDocument(doc);
      setShowDocWindow(true);
    }
  };

  const handleCloseDocument = () => {
    if (activePage === 'chat') {
      removeAllChatDocuments();
    } else {
      setShowDocWindow(false);
      setSelectedDocument(null);
    }
  };

  // Close document window when navigating to examination page with a document
  useEffect(() => {
    if (activePage === 'examination' && selectedDocument && showDocWindow) {
      setShowDocWindow(false);
    }
  }, [activePage, selectedDocument, showDocWindow]);

  const handleCloseMpepViewer = () => {
    setMpepViewerState(null);
    // If on chat page with selected documents, restore document viewer
    if (activePage === 'chat' && selectedChatDocuments.length > 0) {
      setShowDocWindow(true);
    }
  };

  const handleCloseCfrViewer = () => {
    setCfrViewerState(null);
    // If on chat page with selected documents, restore document viewer
    if (activePage === 'chat' && selectedChatDocuments.length > 0) {
      setShowDocWindow(true);
    }
  };

  const handleCloseUscViewer = () => {
    setUscViewerState(null);
    // If on chat page with selected documents, restore document viewer
    if (activePage === 'chat' && selectedChatDocuments.length > 0) {
      setShowDocWindow(true);
    }
  };

  const handleCloseApplicationViewer = () => {
    setApplicationViewerState(null);
    setPdfNumPages(0);
    setPdfScale(1.0);
    // If on chat page with selected documents, restore document viewer
    if (activePage === 'chat' && selectedChatDocuments.length > 0) {
      setShowDocWindow(true);
    }
  };

  // Helper function to fetch OCR text for prosecution documents with polling
  const fetchProsDocText = async (appNumber: string, docId: string, rawDoc: any) => {
    console.log('fetchProsDocText called', { appNumber, docId });

    setApplicationViewerState(prev => prev ? {
      ...prev,
      textLoading: true
    } : null);

    // Poll for text content using the dedicated text endpoint
    taskApi.startGraduatedPolling(
      async () => {
        try {
          console.log('Polling for OCR text...');
          const result = await patentApi.fetchProsecutionDocText(appNumber, docId, rawDoc);
          console.log('Poll result:', result);

          // If we have content, return it to stop polling
          if (result?.content && result.content.trim()) {
            console.log('Got OCR text, stopping poll');
            return result;
          }

          // No content yet, continue polling
          console.log('No content yet, continuing poll...');
          return null;
        } catch (error) {
          console.error('Error polling for OCR text:', error);
          // Continue polling even on error
          return null;
        }
      },
      async (result) => {
        // Handle the result when polling stops or succeeds
        console.log('Polling completed, final result:', result);
        if (result?.content) {
          console.log('Setting OCR text in state');
          setApplicationViewerState(prev => prev ? {
            ...prev,
            ocrText: result.content,
            ocrLines: result.lines || null,
            ocrMetadata: result.metadata || null,
            hasLineNumbers: result.has_line_numbers || false,
            textLoading: false
          } : null);
        } else {
          // Polling completed but no content found
          console.log('No OCR text found after polling timeout');
          setApplicationViewerState(prev => prev ? {
            ...prev,
            ocrText: null,
            ocrLines: null,
            ocrMetadata: null,
            hasLineNumbers: false,
            textLoading: false
          } : null);
        }
      },
      120000 // 2 minute timeout
    );
  };

  // Auto-fetch text when switching prosecution documents if in text/both mode
  useEffect(() => {
    if (applicationViewerState?.prosDocInfo && (prosDocViewMode === 'text' || prosDocViewMode === 'both')) {
      const { appNumber, docId, rawDoc } = applicationViewerState.prosDocInfo;

      // Only fetch if we don't have text yet and aren't currently loading
      if (!applicationViewerState.ocrText && !applicationViewerState.textLoading) {
        console.log('Auto-fetching text for new prosecution document in text/both mode');
        fetchProsDocText(appNumber, docId, rawDoc);
      }
    }
  }, [applicationViewerState?.prosDocInfo?.docId, prosDocViewMode]);

  const handleProsecutionDocClick = async (doc: any) => {
    // For prosecution documents, fetch and display the PDF and OCR text in the sidepane
    console.log('handleProsecutionDocClick called', { doc });
    try {
      const appNumber = applicationViewerState?.applicationData?.application_number;

      if (!appNumber) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Application number not found',
        });
        return;
      }

      const prosDocInfo = {
        appNumber,
        docId: doc.docId,
        rawDoc: doc.rawDoc,
        docName: doc.description || doc.file_name || doc.title || 'Prosecution Document',
        docDate: doc.date || doc.officialDate || undefined
      };
      console.log('Setting state with prosDocInfo:', prosDocInfo);

      // Set loading state for PDF and store doc info (don't auto-fetch text)
      setApplicationViewerState(prev => {
        const newState = {
          ...prev,
          pdfLoading: true,
          pdfUrl: null,
          textLoading: false,
          ocrText: null,
          prosDocInfo
        };
        console.log('New state being set:', newState);
        return prev ? newState : null;
      });
      setPdfNumPages(0);
      setProsDocViewMode('pdf'); // Default to PDF view

      // Don't auto-fetch text - only fetch when user clicks "Text" or "Both"

      // Start the PDF generation job
      const response = await patentApi.getProsecutionDocPdf(appNumber, doc.docId);

      if (response?.job_id) {
        // Start polling for the result
        taskApi.startGraduatedPolling(
          async () => {
            const result = await patentApi.getPdfJobStatus(response.job_id);

            // Check if job is complete
            if (result?.status && result.status.toLowerCase().startsWith('complete')) {
              return result;
            }

            // Return result if there's an error status (stops polling)
            if (result?.status && result.status.toLowerCase().includes('error')) {
              return result;
            }

            // Still processing, return null to continue polling
            return null;
          },
          async (result) => {
            // Handle the result when polling stops
            if (result?.status && result.status.toLowerCase().startsWith('complete')) {
              try {
                // Download the PDF
                const pdfUrl = await patentApi.downloadProsecutionDocPdf(appNumber, doc.docId);
                setApplicationViewerState(prev => prev ? { ...prev, pdfUrl, pdfLoading: false } : null);

                const docName = doc.description || doc.file_name || doc.title || 'Prosecution document';
                addNotification({
                  type: 'success',
                  title: 'Document Ready',
                  message: `${docName} is ready to view`,
                });
              } catch (error) {
                console.error('Error downloading prosecution PDF:', error);
                setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
                addNotification({
                  type: 'error',
                  title: 'Error',
                  message: 'Failed to download prosecution document PDF',
                });
              }
            } else if (result?.status && result.status.toLowerCase().includes('error')) {
              setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
              addNotification({
                type: 'error',
                title: 'Error',
                message: `Failed to load document PDF: ${result.status}`,
              });
            }
          },
          (error) => {
            console.error('Polling error:', error);
            setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
            addNotification({
              type: 'error',
              title: 'Error',
              message: 'Failed to load prosecution document',
            });
          }
        );
      } else {
        setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to start PDF generation',
        });
      }
    } catch (error) {
      console.error('Failed to load prosecution document:', error);
      setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load prosecution document',
      });
    }
  };

  const handleAddApplicationToActiveDocuments = (applicationNumber: string) => {
    try {
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

      // Initialize arrays if needed
      if (!context.current_applications) {
        context.current_applications = [];
      }

      // Check if already added
      if (context.current_applications.includes(applicationNumber)) {
        addNotification({
          type: 'info',
          title: 'Already Added',
          message: `Application ${applicationNumber} is already in session documents.`,
        });
        return;
      }

      // Add to active documents
      context.current_applications.push(applicationNumber);
      localStorage.setItem('session_context', JSON.stringify(context));
      window.dispatchEvent(new Event('storage-update'));

      addNotification({
        type: 'success',
        title: 'Application Added',
        message: `Application ${applicationNumber} has been added to session documents.`,
      });
    } catch (error) {
      console.error('Failed to add application to session documents:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to add application to session documents.',
      });
    }
  };

  const handleNavigateToMpepSection = (sectionNumber: string) => {
    ensureMpepStructure();
    loadMpepSection(sectionNumber, `MPEP §${sectionNumber}`);
  };

  const handleNavigateToCfrSection = (sectionNumber: string) => {
    ensureCfrStructure();
    loadCfrSection(sectionNumber, `37 C.F.R. § ${sectionNumber}`);
  };

  const handleNavigateToUscSection = (sectionNumber: string) => {
    ensureUscStructure();
    loadUscSection(sectionNumber, `35 U.S.C. § ${sectionNumber}`);
  };

  const handleViewApplicationFromDocument = async (applicationNumber: string) => {
    // Find the application in sessionApplications
    const sessionApp = sessionApplications?.find(app =>
      (app.application_id || app.application?.applicationNumberText) === applicationNumber
    );

    // Close other viewers
    setMpepViewerState(null);
    setCfrViewerState(null);

    if (sessionApp && sessionApp.application) {
      // Map session application structure to USApplicationDisplay expected structure
      setApplicationViewerState({
        applicationData: {
          application_number: applicationNumber,
          patent_info: {
            applicationMetaData: sessionApp.application.applicationMetaData || {}
          },
          pgpubDocumentMetaData: sessionApp.application.pgpubDocumentMetaData,
          grantDocumentMetaData: sessionApp.application.grantDocumentMetaData,
        }
      });
    } else {
      // If not found in session, fetch from backend
      try {
        const appData = await patentApi.fetchApplication(applicationNumber);
        setApplicationViewerState({
          applicationData: appData
        });
      } catch (error) {
        console.error('Failed to fetch application data:', error);
        addNotification({
          type: 'error',
          title: 'Failed to Load',
          message: 'Could not load application data',
        });
      }
    }
  };

  // Handle document viewer resize
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      // Calculate new width from right edge
      const newWidth = window.innerWidth - e.clientX;

      // Constrain width between 280px and (100vw - 400px)
      const minWidth = 280; // Reduced from 400px to allow smaller sizes
      const minChatWidth = 400; // Reduced from 700px to allow more flexibility
      const maxWidth = Math.max(minWidth, window.innerWidth - minChatWidth - (sidebarCollapsed ? 0 : compactMode ? 220 : 260));
      const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      setDocumentViewerWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, sidebarCollapsed, compactMode, setDocumentViewerWidth]);

  // Calculate button visibility
  const getActiveDocumentCount = () => {
    try {
      const sessionContextStr = localStorage.getItem('session_context');
      if (!sessionContextStr || sessionContextStr === 'undefined') return 0;

      const sessionContext = JSON.parse(sessionContextStr);
      let count = 0;

      // Count current patents
      if (sessionContext.current_patents && Array.isArray(sessionContext.current_patents)) {
        count += sessionContext.current_patents.length;
      }

      // Count current publications
      if (sessionContext.current_publications && Array.isArray(sessionContext.current_publications)) {
        count += sessionContext.current_publications.length;
      }

      // Count current applications
      if (sessionContext.current_applications && Array.isArray(sessionContext.current_applications)) {
        count += sessionContext.current_applications.length;
      }

      // Count current foreign patents
      if (sessionContext.current_foreign_patents && Array.isArray(sessionContext.current_foreign_patents)) {
        count += sessionContext.current_foreign_patents.length;
      }

      // Count other document types
      if (sessionContext.cfr_sections && Array.isArray(sessionContext.cfr_sections)) {
        count += sessionContext.cfr_sections.length;
      }

      if (sessionContext.mpep_sections && Array.isArray(sessionContext.mpep_sections)) {
        count += sessionContext.mpep_sections.length;
      }

      if (sessionContext.resource_ids && Array.isArray(sessionContext.resource_ids)) {
        count += sessionContext.resource_ids.length;
      }

      return count;
    } catch (error) {
      console.error('Error parsing session_context:', error);
      return 0;
    }
  };

  const hasDocuments = getActiveDocumentCount() > 0;
  const hasContent = chatMessages.length > 1 || hasDocuments;

  // Show both Save and Save As buttons when there's content
  // Save button will open Save As modal for unsaved sessions (handled in handleSave)
  const showSaveButton = hasContent;
  const showSaveAsButton = hasContent;
  const hasUnsavedChanges = sessionIsDirty || (chatMessages.length > 1 && !sessionHasBeenSaved);
  const maxViewerWidth = Math.max(280, viewportWidth - 400 - (activePage === 'chat' && !sidebarCollapsed ? compactMode ? 220 : 260 : 0));
  const visibleViewerWidth = Math.min(documentViewerWidth, maxViewerWidth);

  return (
    <div className={`flex flex-col h-screen bg-slate-50 ${compactMode ? 'compact-mode' : ''}`}>
      {/* Header - Always at top */}
      <Header
        activePage={activePage}
        onPageChange={handlePageChange}
      />
      <ResolutionWarningModal inline />

      {/* Content Area Below Header */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sidebar - Only show on chat page */}
        {activePage === 'chat' && (
          <>
            <Sidebar
              activePage={activePage}
              onPageChange={handlePageChange}
              onDocumentSelect={handleDocumentSelect}
              onAddDocuments={() => setShowDocumentOnboarding(true)}
              onNewSession={handleNewSession}
              onSaveSession={handleSave}
              onSaveAsSession={handleSaveAs}
              showSaveButton={showSaveButton}
              showSaveAsButton={showSaveAsButton}
              collapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />

            {/* Sidebar Collapse Toggle - Positioned on border */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="absolute top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-200 rounded-full h-8 w-8 p-0 shadow-sm hover:shadow-md transition-all"
              style={{
                left: sidebarCollapsed ? '0px' : compactMode ? '204px' : '244px'
              }}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
            </Button>
          </>
        )}

        {/* Main Content Container */}
        <div className="flex-1 flex overflow-hidden">
          {/* Main Content - Changes based on active page */}
          <div className={`${showDocWindow && activePage === 'documents' && selectedDocument ? '' : 'flex-1 min-w-0'} flex flex-col ${showDocWindow && activePage === 'chat' ? 'border-r border-gray-200' : ''}`}>
            <div className={activePage === 'chat' ? 'flex-1 min-h-0 flex flex-col' : 'hidden'}>
              <ChatArea
                activePage="chat"
                visible={activePage === 'chat'}
                onDocumentClick={handleDocumentSelect}
                sidebarCollapsed={sidebarCollapsed}
                hasOpenDocuments={selectedChatDocuments.length > 0}
                onOpenDocumentModal={() => setShowDocumentOnboarding(true)}
              />
            </div>

            {activePage === 'documents' && (
              <DocumentsPage
                onDocumentSelect={handleDocumentSelect}
                isDocumentViewerOpen={showDocWindow && activePage === 'documents' && !!selectedDocument}
                onAddDocuments={() => setShowDocumentOnboarding(true)}
              />
            )}

            {activePage === 'uspto_data' && (
              <USPTODataPage onDocumentSelect={handleDocumentSelect} />
            )}

            {activePage === 'examination' && (
              <ExaminationPage
                key={examinationApplicationNumber || 'default'}
                onDocumentSelect={handleDocumentSelect}
                initialApplicationNumber={examinationApplicationNumber}
                initialDocument={selectedDocument}
              />
            )}

            {activePage === 'resources' && (
              <ResourcesPage
                onDocumentSelect={handleDocumentSelect}
              />
            )}

            {(activePage === 'tasks' || tasksVisited) && (
              <div className={activePage === 'tasks' ? 'flex-1 min-h-0 flex flex-col' : 'hidden'}>
              <TasksPage
                preferredDocument={taskSourceDocument}
                openJobId={tasksOpenJobId}
                onOpenJobIdConsumed={() => setTasksOpenJobId(null)}
                openTaskId={tasksOpenTaskId}
                onOpenTaskIdConsumed={() => setTasksOpenTaskId(null)}
              />
              </div>
            )}

            {activePage === 'ids' && (
              <IDSPage />
            )}

            {activePage === 'forms' && (
              <FormsPage />
            )}

            {activePage === 'templates' && (
              <TemplatesPage />
            )}

            {activePage === 'sessions' && (
              <SessionsPage
                onBack={() => setActivePage('chat')}
              />
            )}

            {activePage === 'drafts' && (
              <DraftsPage
                onBack={() => setActivePage('chat')}
              />
            )}
          </div>

          {/* Document / Reference Viewer - hidden on resources, tasks, forms, and templates pages */}
          {((showDocWindow && activePage !== 'uspto_data') || mpepViewerState || cfrViewerState || uscViewerState || applicationViewerState) && activePage !== 'resources' && activePage !== 'tasks' && activePage !== 'forms' && activePage !== 'templates' && !(activePage === 'documents' && !selectedDocument) && (
            <div
              className="relative flex"
              style={{
                width: activePage === 'documents' ? 'auto' : `${visibleViewerWidth}px`,
                flex: activePage === 'documents' ? 1 : 'none'
              }}
            >
              {/* Resize Handle - only show on chat page */}
              {activePage === 'chat' && (
                <div
                  className="group absolute left-0 top-0 bottom-0 w-1 bg-gray-200 hover:bg-blue-500 cursor-ew-resize transition-colors z-10"
                  onMouseDown={handleResizeStart}
                  role="separator"
                  tabIndex={0}
                  aria-label="Document preview width"
                  aria-orientation="vertical"
                  aria-valuemin={280}
                  aria-valuemax={maxViewerWidth}
                  aria-valuenow={visibleViewerWidth}
                  onKeyDown={event => {
                    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                    event.preventDefault();
                    const width = event.key === 'Home' ? 280 : event.key === 'End' ? maxViewerWidth : visibleViewerWidth + (event.key === 'ArrowLeft' ? 40 : -40);
                    setDocumentViewerWidth(Math.max(280, Math.min(maxViewerWidth, width)));
                  }}
                  style={{
                    backgroundColor: isResizing ? '#3b82f6' : undefined
                  }}
                >
                  {/* Expanded hit area for easier grabbing */}
                  <div className="absolute inset-y-0 -left-2 -right-2" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      {/* Centred on the divider, not hanging off it.
                          It was `top-1/2` with no matching translate, so it sat a half
                          height below the middle of the bar, and `-left-3` pushed the
                          whole thing to one side of a 4px handle. The chevrons were the
                          other half of it: the button base forces `size-4` on any svg
                          whose class does not mention `size-`, so `w-3 h-3` rendered at
                          16px, and the base `gap-2` put 8px between them — two oversized
                          arrows shoved apart inside a 28px box. Hence `size-3` and
                          `gap-0`, and a pill that reads as a grip on the bar. */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-1/2 left-1/2 z-20 flex h-11 w-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-0 rounded-full border-slate-200 bg-white p-0 text-slate-400 shadow-sm transition-colors hover:border-blue-400 hover:bg-white hover:text-blue-600 group-hover:border-blue-400 group-hover:text-blue-600"
                        aria-label="Choose document preview width"
                        onMouseDown={event => event.stopPropagation()}
                      >
                        <ChevronLeft className="size-3" />
                        <ChevronRight className="size-3 -ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onSelect={() => setDocumentViewerWidth(Math.min(360, maxViewerWidth))}>Narrow preview</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setDocumentViewerWidth(Math.min(600, maxViewerWidth))}>Balanced preview</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setDocumentViewerWidth(maxViewerWidth)}>Wide preview</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
              {mpepViewerState ? (
                <MpepViewer
                  section={mpepViewerState.section}
                  title={mpepViewerState.title}
                  content={mpepViewerState.content}
                  loading={mpepViewerState.loading}
                  error={mpepViewerState.error}
                  onClose={handleCloseMpepViewer}
                  onReload={() => loadMpepSection(mpepViewerState.section, mpepViewerState.title)}
                  onSectionSelect={handleNavigateToMpepSection}
                  chapters={mpepStructure}
                  chaptersLoading={mpepStructureLoading}
                />
              ) : cfrViewerState ? (
                <CfrViewer
                  section={cfrViewerState.section}
                  title={cfrViewerState.title}
                  content={cfrViewerState.content}
                  loading={cfrViewerState.loading}
                  error={cfrViewerState.error}
                  onClose={handleCloseCfrViewer}
                  onReload={() => loadCfrSection(cfrViewerState.section, cfrViewerState.title)}
                  onSectionSelect={handleNavigateToCfrSection}
                  structure={cfrStructure || undefined}
                  structureLoading={cfrStructureLoading}
                />
              ) : uscViewerState ? (
                <UscViewer
                  section={uscViewerState.section}
                  title={uscViewerState.title}
                  content={uscViewerState.content}
                  loading={uscViewerState.loading}
                  error={uscViewerState.error}
                  onClose={handleCloseUscViewer}
                  onReload={() => loadUscSection(uscViewerState.section, uscViewerState.title)}
                  onSectionSelect={handleNavigateToUscSection}
                  structure={uscStructure || undefined}
                  structureLoading={uscStructureLoading}
                />
              ) : applicationViewerState ? (
                <div className="w-full h-full bg-white flex flex-col">
                  <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    {!applicationViewerState.hideApplicationHeader && (
                      <div className="flex flex-col">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {applicationViewerState.pdfUrl && applicationViewerState.prosDocInfo?.docName
                            ? applicationViewerState.prosDocInfo.docName
                            : applicationViewerState.pdfUrl
                            ? 'Prosecution Document'
                            : 'Application Data'}
                        </h2>
                        {applicationViewerState.pdfUrl && applicationViewerState.prosDocInfo?.docDate && (
                          <p className="text-sm text-gray-500 mt-1">
                            {applicationViewerState.prosDocInfo.docDate}
                          </p>
                        )}
                      </div>
                    )}
                    {applicationViewerState.hideApplicationHeader && applicationViewerState.pdfUrl && applicationViewerState.prosDocInfo && (
                      <div className="flex flex-col">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {applicationViewerState.prosDocInfo.docName || 'Prosecution Document'}
                        </h2>
                        {applicationViewerState.prosDocInfo.docDate && (
                          <p className="text-sm text-gray-500 mt-1">
                            {applicationViewerState.prosDocInfo.docDate}
                          </p>
                        )}
                      </div>
                    )}
                    <div className={`flex items-center gap-2 ${applicationViewerState.hideApplicationHeader && !applicationViewerState.prosDocInfo ? 'ml-auto' : ''}`}>
                      {!applicationViewerState.hideApplicationHeader && !applicationViewerState.pdfUrl && (() => {
                        const appNumber = applicationViewerState.applicationData?.application_number;
                        const stored = localStorage.getItem('session_context');
                        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
                        const isInActiveDocuments = context.current_applications?.includes(appNumber);

                        return isInActiveDocuments ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            disabled
                            title="Already in Session documents"
                          >
                            <Check className="w-4 h-4 text-green-600" />
                            In this session Documents
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            onClick={() => handleAddApplicationToActiveDocuments(appNumber)}
                          >
                            <Plus className="w-4 h-4" />
                            Add to session
                          </Button>
                        );
                      })()}
                      {applicationViewerState.pdfUrl && (
                        <>
                          <div className="flex items-center gap-1 border border-gray-300 rounded-md p-1">
                            <Button
                              size="sm"
                              variant={prosDocViewMode === 'pdf' ? 'default' : 'ghost'}
                              onClick={() => setProsDocViewMode('pdf')}
                              className="h-7 px-3 text-xs"
                            >
                              PDF
                            </Button>
                            <Button
                              size="sm"
                              variant={prosDocViewMode === 'text' ? 'default' : 'ghost'}
                              onClick={() => {
                                console.log('Text button clicked', {
                                  hasText: !!applicationViewerState.ocrText,
                                  isLoading: applicationViewerState.textLoading,
                                  hasProsDocInfo: !!applicationViewerState.prosDocInfo,
                                  prosDocInfo: applicationViewerState.prosDocInfo
                                });
                                setProsDocViewMode('text');
                                // Retry fetching text if not available and not currently loading
                                if (!applicationViewerState.ocrText && !applicationViewerState.textLoading && applicationViewerState.prosDocInfo) {
                                  console.log('Fetching text...');
                                  const { appNumber, docId, rawDoc } = applicationViewerState.prosDocInfo;
                                  fetchProsDocText(appNumber, docId, rawDoc);
                                } else if (!applicationViewerState.prosDocInfo) {
                                  console.warn('No prosDocInfo available for retry');
                                }
                              }}
                              className="h-7 px-3 text-xs"
                            >
                              Text {applicationViewerState.textLoading && <Loader2 className="w-3 h-3 ml-1 animate-spin" />}
                            </Button>
                            <Button
                              size="sm"
                              variant={prosDocViewMode === 'both' ? 'default' : 'ghost'}
                              onClick={() => {
                                console.log('Both button clicked', {
                                  hasText: !!applicationViewerState.ocrText,
                                  isLoading: applicationViewerState.textLoading,
                                  hasProsDocInfo: !!applicationViewerState.prosDocInfo
                                });
                                setProsDocViewMode('both');
                                // Retry fetching text if not available and not currently loading
                                if (!applicationViewerState.ocrText && !applicationViewerState.textLoading && applicationViewerState.prosDocInfo) {
                                  console.log('Fetching text...');
                                  const { appNumber, docId, rawDoc } = applicationViewerState.prosDocInfo;
                                  fetchProsDocText(appNumber, docId, rawDoc);
                                } else if (!applicationViewerState.prosDocInfo) {
                                  console.warn('No prosDocInfo available for retry');
                                }
                              }}
                              className="h-7 px-3 text-xs"
                            >
                              Both {applicationViewerState.textLoading && <Loader2 className="w-3 h-3 ml-1 animate-spin" />}
                            </Button>
                          </div>
                          {!applicationViewerState.hideApplicationHeader && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setApplicationViewerState(prev => prev ? { ...prev, pdfUrl: null, pdfLoading: false, ocrText: null, textLoading: false } : null);
                                setProsDocViewMode('pdf');
                              }}
                            >
                              Back to Application
                            </Button>
                          )}
                        </>
                      )}
                      <button
                        onClick={handleCloseApplicationViewer}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto">
                    {applicationViewerState.pdfLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Loading prosecution document...</span>
                        </div>
                      </div>
                    ) : applicationViewerState.pdfUrl ? (
                      <div className={`flex h-full ${prosDocViewMode === 'both' ? 'flex-row' : 'flex-col'}`}>
                        {/* PDF View */}
                        {(prosDocViewMode === 'pdf' || prosDocViewMode === 'both') && (
                          <div className={`flex flex-col items-center p-4 overflow-auto ${prosDocViewMode === 'both' ? 'w-1/2 border-r border-gray-200' : 'w-full'}`}>
                            <div className="mb-4 flex gap-2">
                              <Button size="sm" onClick={() => setPdfScale(Math.max(0.5, pdfScale - 0.1))}>
                                Zoom Out
                              </Button>
                              <Button size="sm" onClick={() => setPdfScale(1.0)}>
                                Reset ({Math.round(pdfScale * 100)}%)
                              </Button>
                              <Button size="sm" onClick={() => setPdfScale(Math.min(2.0, pdfScale + 0.1))}>
                                Zoom In
                              </Button>
                            </div>
                            <div className="w-full max-w-[900px]">
                              <Document
                                file={applicationViewerState.pdfUrl}
                                onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                                onLoadError={(error) => {
                                  console.error('Error loading PDF:', error);
                                  addNotification({
                                    type: 'error',
                                    title: 'Error',
                                    message: 'Failed to load PDF',
                                  });
                                }}
                                loading={
                                  <div className="flex items-center justify-center p-8">
                                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                  </div>
                                }
                              >
                                {Array.from(new Array(pdfNumPages), (el, index) => (
                                  <Page
                                    key={`page_${index + 1}`}
                                    pageNumber={index + 1}
                                    scale={pdfScale}
                                    className="mb-4 shadow-lg"
                                  />
                                ))}
                              </Document>
                            </div>
                          </div>
                        )}

                        {/* Text View */}
                        {(prosDocViewMode === 'text' || prosDocViewMode === 'both') && (
                          <div className={`flex flex-col overflow-auto relative min-h-[500px] ${prosDocViewMode === 'both' ? 'w-1/2' : 'w-full'}`}>
                            {applicationViewerState.textLoading ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Loading OCR text...</span>
                                </div>
                              </div>
                            ) : null}
                            {applicationViewerState.ocrText ? (
                              applicationViewerState.hasLineNumbers && applicationViewerState.ocrLines ? (
                                <LineNumberedOAViewer
                                  lines={applicationViewerState.ocrLines}
                                  searchQuery=""
                                />
                              ) : (
                                <div className="prose prose-sm max-w-none p-6">
                                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-800 bg-gray-50 p-4 rounded-lg">
                                    {applicationViewerState.ocrText}
                                  </pre>
                                </div>
                              )
                            ) : !applicationViewerState.textLoading ? (
                              <div className="flex items-center justify-center h-full text-gray-500">
                                <p>No OCR text available for this document.</p>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ) : (
                      <USApplicationDisplay
                        applicationData={applicationViewerState.applicationData}
                        onDocumentClick={handleDocumentSelect}
                        onProsecutionDocClick={handleProsecutionDocClick}
                      />
                    )}
                  </div>
                </div>
              ) : (
                showDocWindow && (
                  activePage === 'chat' && selectedChatDocuments.length > 0 ? (
                    // Multi-document view for chat page
                    <div className="h-full flex flex-col bg-white flex-1">
                      {/* Header with document management */}
                      <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between bg-gray-50">
                        <div className="flex items-center gap-3 flex-wrap">
                          <BookOpen className="w-5 h-5 text-purple-600" />
                          <span className="font-semibold text-gray-900">
                            {selectedChatDocuments.length} Document{selectedChatDocuments.length > 1 ? 's' : ''}
                          </span>
                          {selectedChatDocuments.map((chatDoc) => (
                            <div key={chatDoc.id} className="flex items-center gap-1 bg-white border border-gray-300 rounded px-2 py-1">
                              <span className="text-xs truncate max-w-[120px]">
                                {chatDoc.doc.file_name || chatDoc.doc.title || 'Document'}
                              </span>
                              <button
                                onClick={() => removeChatDocument(chatDoc.id)}
                                className="text-gray-500 hover:text-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.dispatchEvent(new Event('open-document-panel'))}
                            className="h-7 px-2"
                            title="Add document"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedChatDocuments.length >= 2 && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm">
                                  Layout
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => setChatDocumentArrangement('horizontal')}>
                                  Side-by-Side
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setChatDocumentArrangement('vertical')}>
                                  Stacked
                                </DropdownMenuItem>
                                {selectedChatDocuments.length >= 3 && (
                                  <DropdownMenuItem onClick={() => setChatDocumentArrangement('grid')}>
                                    Grid
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={removeAllChatDocuments}
                          >
                            Close All
                          </Button>
                        </div>
                      </div>

                      {/* Documents area */}
                      <div className="flex-1 overflow-hidden">
                        {selectedChatDocuments.length === 1 ? (
                          <DocumentViewer
                            document={selectedChatDocuments[0].doc}
                            onClose={() => removeChatDocument(selectedChatDocuments[0].id)}
                            isFullWidth={false}
                            onViewApplication={handleViewApplicationFromDocument}
                            initialTab={selectedChatDocuments[0].doc?.initialTab}
                            scrollToId={selectedChatDocuments[0].doc?.scrollToId}
                          />
                        ) : chatDocumentArrangement === 'horizontal' ? (
                          <div className="flex h-full">
                            {selectedChatDocuments.map((chatDoc) => (
                              <div key={chatDoc.id} className="flex-1 border-r last:border-r-0 border-gray-200 overflow-hidden">
                                <DocumentViewer
                                  document={chatDoc.doc}
                                  onClose={() => removeChatDocument(chatDoc.id)}
                                  isFullWidth={false}
                                  onViewApplication={handleViewApplicationFromDocument}
                                  initialTab={chatDoc.doc?.initialTab}
                                  scrollToId={chatDoc.doc?.scrollToId}
                                />
                              </div>
                            ))}
                          </div>
                        ) : chatDocumentArrangement === 'vertical' ? (
                          <div className="flex flex-col h-full">
                            {selectedChatDocuments.map((chatDoc) => (
                              <div key={chatDoc.id} className="flex-1 border-b last:border-b-0 border-gray-200 overflow-hidden">
                                <DocumentViewer
                                  document={chatDoc.doc}
                                  onClose={() => removeChatDocument(chatDoc.id)}
                                  isFullWidth={false}
                                  onViewApplication={handleViewApplicationFromDocument}
                                  initialTab={chatDoc.doc?.initialTab}
                                  scrollToId={chatDoc.doc?.scrollToId}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-px h-full bg-gray-200">
                            {selectedChatDocuments.map((chatDoc) => (
                              <div key={chatDoc.id} className="bg-white overflow-hidden">
                                <DocumentViewer
                                  document={chatDoc.doc}
                                  onClose={() => removeChatDocument(chatDoc.id)}
                                  isFullWidth={false}
                                  onViewApplication={handleViewApplicationFromDocument}
                                  initialTab={chatDoc.doc?.initialTab}
                                  scrollToId={chatDoc.doc?.scrollToId}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Single document view for non-chat pages
                    <DocumentViewer
                      document={selectedDocument}
                      onClose={() => {
                        handleCloseDocument();
                        setPendingTaskResult(null);
                      }}
                      isFullWidth={activePage === 'documents'}
                      onViewApplication={handleViewApplicationFromDocument}
                      initialTab={selectedDocument?.initialTab}
                      scrollToId={selectedDocument?.scrollToId}
                      initialTaskResult={pendingTaskResult}
                    />
                  )
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Session Expired Modal */}
      <Dialog open={showSessionExpiredModal} onOpenChange={setShowSessionExpiredModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Session Expired</DialogTitle>
            <DialogDescription className="text-gray-600">
              {sessionExpiredMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleSessionExpiredConfirm} className="w-full sm:w-auto">
              Login Again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications */}
      <Notifications />

      {/* Document Onboarding Modal */}
      {showDocumentOnboarding && (
        <DocumentOnboarding
          onClose={() => setShowDocumentOnboarding(false)}
          contextData={sessionContext || {}}
          sessionApplications={sessionApplications || []}
          onDocumentAdded={() => {
            // TODO: Refresh documents list
            console.log('Document added');
          }}
          onOpenUploadModal={() => {
            setShowDocumentOnboarding(false);
            setShowFileUploadModal(true);
          }}
          onSwitchToBatch={() => {
            setShowDocumentOnboarding(false);
            setShowBatchDocumentOnboarding(true);
          }}
        />
      )}

      {/* Batch Document Onboarding Modal */}
      {showBatchDocumentOnboarding && (
        <BatchDocumentOnboarding
          onClose={() => setShowBatchDocumentOnboarding(false)}
          contextData={sessionContext || {}}
          onDocumentAdded={() => {
            // TODO: Refresh documents list
            console.log('Document added');
          }}
          onSwitchToSingle={() => {
            setShowBatchDocumentOnboarding(false);
            setShowDocumentOnboarding(true);
          }}
        />
      )}

      {/* File Upload Modal */}
      <FileUploadModal
        open={showFileUploadModal}
        onOpenChange={setShowFileUploadModal}
      />

      {/* Save Session Modal */}
      <SaveSessionModal
        open={showSaveModal}
        onOpenChange={setShowSaveModal}
        saveAsMode={saveAsMode}
      />

      {/* New Session Warning Modal */}
      <NewSessionWarningModal
        open={showNewSessionWarning}
        onOpenChange={setShowNewSessionWarning}
        onConfirm={handleNewSessionConfirm}
        onSaveFirst={handleNewSessionSaveFirst}
        hasUnsavedChanges={hasUnsavedChanges}
        hasDocuments={hasDocuments}
        documentCount={getActiveDocumentCount()}
      />

      {/* Onboarding Wizard */}
      <OnboardingWizard
        open={showOnboardingWizard}
        onOpenChange={setShowOnboardingWizard}
        sessionContext={{ ...sessionContext, session_applications: sessionApplications }}
        onPasteQuestion={handlePasteQuestionFromWizard}
        onOpenDocumentModal={handleOpenDocumentModalFromWizard}
        onOpenUploadModal={handleOpenUploadModalFromWizard}
        onNavigateToPage={handleNavigateToPageFromWizard}
        onAddApplication={handleAddApplicationFromWizard}
        onSaveSession={() => setShowSaveModal(true)}
      />

      {/* Quick Start Screen */}
      <QuickStartScreen
        open={showQuickStart}
        onOpenChange={setShowQuickStart}
        onStartChat={() => {
          setActivePage('chat');
          setShowQuickStart(false);
        }}
        onOpenDocumentModal={handleOpenDocumentModalFromWizard}
        onOpenUploadModal={handleOpenUploadModalFromWizard}
        onNavigateToPage={handleNavigateToPageFromWizard}
        onOpenWizard={() => {
          setShowQuickStart(false);
          setShowOnboardingWizard(true);
        }}
      />
    </div>
  );
}
